import React from 'react';
import { CargoOperationViewModel } from 'src/types/ViewModel/Stowage/CargoOperation/CargoOperationViewModel';
import {
    DefaultFlexStrechedWrapper,
    DefaultPanelWrapper,
    ElementRangeStyle,
    OptionsElementWrapper,
    IntervalWrapper,
    PanelSectionContent,
    PanelSubHeader,
    ElementWrapper,
} from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { getOptionsList, getOptionMinusValue, getOptionPlusValue } from '../utils/cargoOperationDisplayUtils';
import { getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import { ValidationItem } from 'src/types/ViewModel/ValidationItemType';
import ErrorInfoIcon from 'src/components/ui/status-icon/WarningInfoIcon';
import { isNullOrUndefined } from 'src/utils/objectUtils';

interface CargoSectionProps {
    cargoOperationVM: CargoOperationViewModel;
    isFieldReadOnly: (field: string) => boolean;
    onCargoInputChange: (changeObject: InputChangeData) => void;
}

export const CargoSection: React.FC<CargoSectionProps> = ({ cargoOperationVM, isFieldReadOnly, onCargoInputChange }) => {
    const getOptionUnit = () => {
        return cargoOperationVM.cargo.optionType !== 'Min / Max' ? '%' : 'MT';
    };

    const customValidations: ValidationItem[] = [
        {
            validationFunction: () =>
                cargoOperationVM.totalNominatedWeight < cargoOperationVM.totalSwingFromWeight ||
                cargoOperationVM.totalNominatedWeight > cargoOperationVM.totalSwingToWeight,
            errorMessage: 'Total nominated weight outside option swing range',
        },
    ];

    return (
        <PanelSectionContent>
            <PanelSubHeader>
                Cargo{' '}
                {getFormattedCargoNumber(
                    cargoOperationVM.cargo.voyageNumber,
                    cargoOperationVM.cargo.cargoNumber,
                    isManualCargo(cargoOperationVM.cargo)
                )}
            </PanelSubHeader>
            <DefaultFlexStrechedWrapper alignTop noMargin className="highlight-panel">
                <DefaultPanelWrapper flexWidth="48%" noMargin>
                    <OptionsElementWrapper noMargin>
                        <span className="select-input">
                            <OdfjellInputComponent
                                isReadOnly={isFieldReadOnly('optionType')}
                                type={'select'}
                                description={'Option'}
                                options={getOptionsList()}
                                value={cargoOperationVM.cargo ? cargoOperationVM.cargo.optionType : null}
                                name={'optionType'}
                                dataObject={cargoOperationVM.cargo}
                                leaveBlank={true}
                                onChange={onCargoInputChange}
                                tabIndex={2}
                                disableUnit={true}
                                labelMinWidth={140}
                                textAlign="left"
                            />
                        </span>
                        <ElementRangeStyle>
                            <span>-</span>
                            <OdfjellInputComponent
                                isReadOnly={isFieldReadOnly('optionMinusPercentage')}
                                type={'number'}
                                value={
                                    !isNullOrUndefined(cargoOperationVM.cargo.optionMinusPercentage)
                                        ? Number(getOptionMinusValue(cargoOperationVM))
                                        : 'N/A'
                                }
                                name={'optionMinusPercentage'}
                                dataObject={cargoOperationVM.cargo}
                                leaveBlank={true}
                                onChange={onCargoInputChange}
                                tabIndex={3}
                                unit={getOptionUnit()}
                                inputSize="small"
                            />
                            <span className="range-separator">/</span>
                            <OdfjellInputComponent
                                isReadOnly={isFieldReadOnly('optionPlusPercentage')}
                                type={'number'}
                                value={
                                    !isNullOrUndefined(cargoOperationVM.cargo.optionPlusPercentage)
                                        ? Number(getOptionPlusValue(cargoOperationVM))
                                        : 'N/A'
                                }
                                name={'optionPlusPercentage'}
                                dataObject={cargoOperationVM.cargo}
                                leaveBlank={true}
                                onChange={onCargoInputChange}
                                tabIndex={4}
                                unit={getOptionUnit()}
                                inputSize="small"
                            />
                        </ElementRangeStyle>
                    </OptionsElementWrapper>
                </DefaultPanelWrapper>

                <DefaultPanelWrapper flexWidth="48%" noMargin>
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'number'}
                        description={'Total contract weight'}
                        value={roundTo3Decimals(cargoOperationVM.totalContractWeight).toFixed(3)}
                        name={'totalContractWeight'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        unit={'MT'}
                        inputSize="large"
                    />
                    <ElementWrapper>
                        <OdfjellInputComponent
                            isReadOnly={true}
                            type={'number'}
                            description={'Total nominated weight'}
                            value={roundTo3Decimals(cargoOperationVM.totalNominatedWeight).toFixed(3)}
                            name={'totalNominatedWeight'}
                            dataObject={cargoOperationVM}
                            leaveBlank={true}
                            unit={'MT'}
                            inputSize="large"
                            validationInTooltip={true}
                            customValidations={customValidations}
                        />
                        {cargoOperationVM.totalNominatedWeight > 0 && !cargoOperationVM.hasMaxTotalNominatedWeight && (
                            <ErrorInfoIcon isReadOnly isWarning tooltipText="Total nominated weight not equal to maximum option weight" />
                        )}
                    </ElementWrapper>
                    <OdfjellInputComponent
                        isReadOnly={true}
                        type={'number'}
                        description={'Total nominated volume'}
                        value={roundTo3Decimals(cargoOperationVM.totalNominatedVolume).toFixed(3)}
                        name={'totalNominatedVolume'}
                        dataObject={cargoOperationVM}
                        leaveBlank={true}
                        unit={'m³'}
                        inputSize="large"
                    />
                    <DefaultFlexStrechedWrapper alignTop noMargin>
                        <span className="first-input">Total swing</span>
                        <IntervalWrapper>
                            <span>[</span>
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                value={roundTo3Decimals(cargoOperationVM.totalSwingFromWeight).toFixed(3)}
                                leaveBlank={true}
                                unit={'MT'}
                                inputSize="large"
                            />
                            <span>,</span>
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                value={roundTo3Decimals(cargoOperationVM.totalSwingToWeight).toFixed(3)}
                                leaveBlank={true}
                                unit={'MT'}
                                inputSize="large"
                            />
                            <span>]</span>
                        </IntervalWrapper>
                    </DefaultFlexStrechedWrapper>
                </DefaultPanelWrapper>
            </DefaultFlexStrechedWrapper>
        </PanelSectionContent>
    );
};
